/**
 * This file is a candidate for splitting out into multiple sub-groups as
 * there's so many tracking events.
 */
import { UpdateClarityScreenShot } from './clarity'
import { getPageContext } from './page-context'
import { formatAsISOString } from '~/utils/dates/formatters'

import type { UseAuthentication } from '~/types/rental-api-aliases'
import type { SignupTrackArgType } from '~/types/segment'
import type { SegmentTypes } from '~/types/api/rental-segment-types'

import type {
  FinancialCommon,
  Nullable,
} from '~/types'

import type {
  TrackBookingPaymentInfoSubmittedParams,
  TrackListingSetViewedParams,
  TrackListingViewedParams,
  TrackPageViewedParams,
  TrackRequestBookingSentParams,
  TrackSearchViewedParams,
} from '~/types/tracking'

export { finalizePageContext, setPageContextFromRoute } from './page-context'

/**
 * Tracks a page view.
 *
 * @param params The parameters of the page view.
 */
export function trackPageViewed(params: TrackPageViewedParams) {
  datadogStartView({ to: params.to, from: params.from })
  UpdateClarityScreenShot(params.from.name?.toString())
  openAIAdsTrack('page_viewed', {
    type: 'contents',
    contents: [
      {
        content_type: 'page',
        name: params.to.name,
      }],
  })
}

let lastTrackedPageUrl: Nullable<string> = null

/**
 * Tracks the Segment page view. Runs after the page has rendered,
 * so the title is the new page's title.
 *
 * @param $config The runtime config
 */
export function trackSegmentPageViewed($config: TrackPageViewedParams['$config']) {
  const enableSegmentPageViewTracking = (!$config.target || $config.target === 'prod') && !isMetaDev
  const page = getPageContext()

  if (!enableSegmentPageViewTracking || !page || page.url === lastTrackedPageUrl) {
    return
  }

  lastTrackedPageUrl = page.url
  segmentPage(page, $config)
}

export function trackUserAuthentication(user: UseAuthentication, rtSessionId: string) {
  datadogSetUser({ user })
  segmentIdentify(user)
  SendIdentifiersToClarity({ customId: user.Email!, customSessionId: rtSessionId })
  openAIAdsTrack('custom', {
    type: 'custom',
    contents: [{
      content_type: 'user',
      customSessionId: rtSessionId,
    }],
  },
  {
    custom_event_name: 'userauthenticated',
  })
}

export function trackUserDeauthentication() {
  datadogRemoveUser()
  segmentReset()
}

export function trackAction(name: string, context?: unknown) {
  datadogAddAction(name, context)
}

export function trackAffiliateLinkClicked(data: SegmentTypes['AffiliateLinkClicked [v1]']) {
  trackSegmentEvent('AffiliateLinkClicked [v1]', data)
}

export function trackAppBannerClicked(data: SegmentTypes['AppBannerClicked [v2]']) {
  trackSegmentEvent('AppBannerClicked [v2]', data)
}

export function trackAppBannerClosed(data: SegmentTypes['AppBannerClosed [v2]']) {
  trackSegmentEvent('AppBannerClosed [v2]', data)
}

export function trackBookingDetailsViewed(data: SegmentTypes['BookingDetailsViewed [v4]']) {
  trackSegmentEvent('BookingDetailsViewed [v4]', data)
}

/**
 * Tracks a booking payment form viewed.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/1856667693/BookingPaymentFormViewed
 * @param data The data to track the booking payment form viewed
 * @returns A promise that resolves when the booking payment form viewed is tracked
 */
export function trackBookingPaymentFormViewed(data: SegmentTypes['BookingPaymentFormViewed [v6]']) {
  trackSegmentEvent('BookingPaymentFormViewed [v6]', data)
}

export function trackBookingPaymentInfoSubmitClicked(data: SegmentTypes['BookingPaymentInfoSubmitClicked [v3]']) {
  trackSegmentEvent('BookingPaymentInfoSubmitClicked [v3]', data)
}

/**
 * Tracks a booking payment info submitted.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/1858469937/BookingPaymentInfoSubmitted
 * @param data The data to track the booking payment info submitted
 * @param googleAdsId The Google Ads ID to track the booking payment info submitted
 * @returns A promise that resolves when the booking payment info submitted is tracked
 */
export function trackBookingPaymentInfoSubmitted(data: TrackBookingPaymentInfoSubmittedParams, googleAdsId: string) {
  trackSegmentEvent('BookingPaymentInfoSubmitted [v7]', data.params)
  trackSegmentEvent(
    'Order Completed',
    {
      // @ts-expect-error Allowing for missing properties so the violations can be seen into Segment
      checkout_id: data.params.bookingId?.toString(),
      // @ts-expect-error Allowing for missing properties so the violations can be seen into Segment
      order_id: data.params.bookingId?.toString(),
      affiliation: data.params.paymentProvider,
      total: roundToNearestDecimal(data.params.bookingValue ?? 0, 2),
      discount: roundToNearestDecimal(data.params.promoDollarValue ?? 0, 2),
      coupon: data.params.promoCode,
      currency: data.params.currency,
      products: [
        {
          // @ts-expect-error Allowing for missing properties so the violations can be seen into Segment
          product_id: data.params.rvId,
          sku: data.params.rvId,
          category: data.params.rvType,
          name: data.additionalParameters.rvName,
          brand: data.additionalParameters.rvMake,
          price: roundToNearestDecimal(data.params.smartNightlyRate ?? data.params.nightlyRate, 2),
          quantity: data.params.tripLength,
        },
      ],
    },
  )

  googleRemarketingTrack({
    event: 'purchase',
    data: {
      send_to: googleAdsId,
      value: roundToNearestDecimal(data.params.bookingValue ?? 0, 2),
      items: [
        {
          id: data.params.rvId,
          google_business_vertical: 'hotel_rental',
        },
      ],
    },
  })

  adalyserTrack({ event: 'lce3' })

  openAIAdsTrack('order_created', {
    type: 'contents',
    amount: roundToNearestCent(data.params.bookingValue ?? 0) * 100,
    currency: data.params.currency,
    contents: [{
      id: data.params.rvId,
      quantity: 1,
      content_type: 'product',
    }],
  }, {
    event_id: !data.params.bookingId ? String(data.params.bookingId) : undefined,
  })
}

export function trackBookingPaymentInfoSubmitFailed(data: SegmentTypes['BookingPaymentInfoSubmitFailed [v1]']) {
  trackSegmentEvent('BookingPaymentInfoSubmitFailed [v1]', data)
}

export function trackCalendarImportFieldClicked(data: SegmentTypes['CalendarImportFieldClicked [v1]']) {
  trackSegmentEvent('CalendarImportModalViewed [v1]', data)
}

export function trackCalendarImportModalViewed(data: SegmentTypes['CalendarImportModalViewed [v1]']) {
  trackSegmentEvent('CalendarImportModalViewed [v1]', data)
}

export function trackCalendarImportSubmitted(data: SegmentTypes['CalendarImportSubmitted [v1]']) {
  trackSegmentEvent('CalendarImportSubmitted [v1]', data)
}

export function trackCalendarConnectModalViewed(data: SegmentTypes['CalendarConnectModalViewed [v1]']) {
  trackSegmentEvent('CalendarConnectModalViewed [v1]', data)
}

export function trackCalendarConnectCtaClicked(data: SegmentTypes['CalendarConnectCtaClicked [v1]']) {
  trackSegmentEvent('CalendarConnectCtaClicked [v1]', data)
}

export function trackCalendarConnectLinkCopied(data: SegmentTypes['CalendarConnectLinkCopied [v1]']) {
  trackSegmentEvent('CalendarConnectLinkCopied [v1]', data)
}

export function trackCalendarUpdateSuccessful(data: SegmentTypes['CalendarUpdateSuccessful [v3]']) {
  trackSegmentEvent('CalendarUpdateSuccessful [v3]', data)
}

export function trackCTAClicked(data: SegmentTypes['CTAClicked [v1]']) {
  trackSegmentEvent('CTAClicked [v1]', data)
}

export function trackDeliveryEducationalModalClicked(data: SegmentTypes['DeliveryEducationalModalClicked [v1]']) {
  trackSegmentEvent('DeliveryEducationalModalClicked [v1]', data)
}

export function trackDisputeChargesClicked(data: SegmentTypes['DisputeChargesClicked [v1]']) {
  trackSegmentEvent('DisputeChargesClicked [v1]', data)
}

export function trackEditListingSaved(data: SegmentTypes['EditListingSaved [v1]']) {
  trackSegmentEvent('EditListingSaved [v1]', data)
}

export function trackEditListingViewed(data: SegmentTypes['EditListingViewed [v2]']) {
  trackSegmentEvent('EditListingViewed [v2]', data)
}

export function trackExperimentViewed(data: SegmentTypes['ExperimentViewed [v1]']) {
  trackSegmentEvent('ExperimentViewed [v1]', data)
}

export function trackExternalLinkClicked(data: SegmentTypes['ExternalLinkClicked [v2]']) {
  trackSegmentEvent('ExternalLinkClicked [v2]', data)
}

export function trackHelpCenterClicked(data: SegmentTypes['HelpCenterClicked [v1]']) {
  trackSegmentEvent('HelpCenterClicked [v1]', data)
}

export function trackListingClicked(data: SegmentTypes['ListingClicked [v7]']) {
  trackSegmentEvent('ListingClicked [v7]', data)
}

export function trackListingSetPresented(params: SegmentTypes['ListingSetPresented [v4]']) {
  trackSegmentEvent('ListingSetPresented [v4]', params)
}

export function trackListingSetViewed(data: TrackListingSetViewedParams) {
  trackSegmentEvent('ListingSetViewed [v5]', data.params)
  trackSegmentEvent(
    'Product List Viewed',
    {
      list_id: data.params.listingPageNumber?.toString(),
      category: data.params.cta,
      products: data.params.listings?.map((rv: SegmentTypes['ListingSetViewed [v5]']['listings'][0], index) => ({
        product_id: rv.rvId,
        sku: rv.rvId,
        category: rv.listingType,
        name: rv.rvName,
        currency: rv.currency,
        price: roundToNearestDecimal(rv.smartNightlyRate ?? rv.nightlyRate, 2),
        quantity: 1,
        position: index,
        url: data.additionalParameters.rvUrlListings[index]?.rvUrl ?? '',
        image_url: rv.heroImage,
      })),
    },
  )
  openAIAdsTrack('contents_viewed', {
    type: 'contents',
    contents: data.params.listings?.map((rv: SegmentTypes['ListingSetViewed [v5]']['listings'][0]) => ({
      id: rv.rvId,
      amount: roundToNearestCent(rv.smartNightlyRate ?? rv.nightlyRate) * 100,
      currency: rv.currency,
      name: rv.rvName,
      content_type: 'product',
    })),
  })
}

export function trackListingStepSubmitted(data: SegmentTypes['ListingStepSubmitted [v2]']) {
  trackSegmentEvent('ListingStepSubmitted [v2]', data)
}

/**
 * Tracks a listing viewed.
 *
 * @see https://rvezy.atlassian.net/wiki/spaaces/DATA/pages/2038202369/ListingViewed
 * @param data The data to track the listing viewed
 * @param googleAdsId The Google Ads ID to track the listing viewed
 * @returns A promise that resolves when the listing viewed is tracked
 */
export function trackListingViewed(data: TrackListingViewedParams, googleAdsId: string) {
  trackSegmentEvent('ListingViewed [v3]', data.params)
  trackSegmentEvent('Product Viewed', {
    product_id: data.params.rvId,
    sku: data.additionalParams.productId,
    category: data.params.rvType,
    // @ts-expect-error Allowing for missing properties so the violations can be seen into Segment
    name: data.params.listingName,
    price: roundToNearestDecimal(data.params.listingBasePrice ?? 0, 2),
    value: roundToNearestDecimal(data.params.totalPrice ?? data.params.listingBasePrice ?? 0, 2),
    quantity: 1,
    // @ts-expect-error Allowing for missing properties so the violations can be seen into Segment
    currency: data.params.currency,
    url: data.additionalParams.rvUrl,
    brand: data.additionalParams.brand,
  })

  googleRemarketingTrack({
    event: 'view_item',
    data: {
      send_to: googleAdsId,
      value: roundToNearestDecimal(data.params.totalPrice ?? data.params.listingBasePrice ?? 0, 2),
      items: [
        {
          id: data.params.rvId,
          start_date: data.additionalParams.startDate,
          end_date: data.additionalParams.endDate,
          google_business_vertical: 'hotel_rental',
        },
      ],
    },
  })

  openAIAdsTrack('contents_viewed', {
    type: 'contents',
    contents: [
      {
        id: data.params.rvId,
        amount: roundToNearestCent(data.params.totalPrice ?? data.params.listingBasePrice ?? 0) * 100,
        currency: data.params.currency,
        name: data.params.listingName,
        content_type: 'product',
      },
    ],
  })
}

export function trackMessageTemplateSelected(data: SegmentTypes['MessageTemplateSelected [v2]']) {
  trackSegmentEvent('MessageTemplateSelected [v2]', data)
}

export function trackOwnerCalendarViewed(data: SegmentTypes['OwnerCalendarViewed [v1]']) {
  trackSegmentEvent('OwnerCalendarViewed [v1]', data)
}

export function trackOwnerCancellationModalClosed(data: SegmentTypes['OwnerCancellationModalClosed [v1]']) {
  trackSegmentEvent('OwnerCancellationModalClosed [v1]', data)
}

export function trackOwnerCancellationModalViewed(data: SegmentTypes['OwnerCancellationModalViewed [v1]']) {
  trackSegmentEvent('OwnerCancellationModalViewed [v1]', data)
}

export function trackPayoutGuaranteeModalViewed(data: SegmentTypes['PayoutGuaranteeModalViewed [v1]']) {
  trackSegmentEvent('PayoutGuaranteeModalViewed [v1]', data)
}

export function trackPaymentUpdateSubmitted(data: SegmentTypes['PaymentUpdateSubmitted [v2]']) {
  trackSegmentEvent('PaymentUpdateSubmitted [v2]', data)
}

export function trackPaymentUpdateSuccessful(data: SegmentTypes['PaymentUpdateSuccessful [v2]']) {
  trackSegmentEvent('PaymentUpdateSuccessful [v2]', data)
}

/**
 * Tracks a payment update viewed.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2312044545/PaymentUpdateViewed
 * @param data The data to track the payment update viewed
 * @returns A promise that resolves when the payment update viewed is tracked
 */
export function trackPaymentUpdateViewed(data: SegmentTypes['PaymentUpdateViewed [v2]']) {
  trackSegmentEvent('PaymentUpdateViewed [v2]', data)
}

export function trackPointOfInterestViewed(data: SegmentTypes['PointOfInterestViewed [v1]']) {
  trackSegmentEvent('PointOfInterestViewed [v1]', data)
}

export function trackProfileViewed(data: SegmentTypes['ProfileViewed [v1]']) {
  trackSegmentEvent('ProfileViewed [v1]', data)
}

/**
 * Tracks a refund method selected.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2326790145/refundMethodSelected
 * @param data The data to track the refund method selected
 * @returns A promise that resolves when the refund method selected is tracked
 */
export function trackRefundMethodSelected(data: SegmentTypes['refundMethodSelected [v1]']) {
  trackSegmentEvent('refundMethodSelected [v1]', data)
}

export async function trackSearchViewed(data: TrackSearchViewedParams, googleAdsId: string) {
  const brazeApi = await useBraze()

  trackSegmentEvent('SearchViewed [v8]', data.params)

  // Todo: Future improvement: Review the data sending to Product Searched event
  trackSegmentEvent('Products Searched', {
    query: JSON.stringify(data.params),
  })

  // When no dates have been selected, or the total amount of results is below
  // 30, we send a custom event to Braze.
  if ((!data.params.startDate && !data.params.endDate) || (data.additionalParams?.length ?? 0) < 30) {
    brazeApi.logCustomEvent('InApp_SearchViewed', data.params)
  }

  googleRemarketingTrack({
    event: 'view_search_results',
    data: {
      send_to: googleAdsId,
      value: 0,
      items: data.additionalParams?.map((rv) => ({
        id: rv.id,
        start_date: data.params.startDate,
        end_date: data.params.endDate,
        google_business_vertical: 'hotel_rental',
      })),
    },
  })
}

export function trackShareButtonClicked(data: SegmentTypes['ShareButtonClicked [v1]']) {
  trackSegmentEvent('ShareButtonClicked [v1]', data)
}

export function trackSignupExited(data: SegmentTypes['SignupExited [v2]']) {
  trackSegmentEvent('SignupExited [v2]', data)
}

export function trackSignupFailed(data: SegmentTypes['SignupFailed [v1]']) {
  trackSegmentEvent('SignupFailed [v1]', data)
}

export function trackSignupViewed(data: SegmentTypes['SignupViewed [v1]']) {
  trackSegmentEvent('SignupViewed [v1]', data)
}

export function trackSortMenuClosed() {
  trackSegmentEvent('SortMenuClosed', {})
}

export function trackSortMenuViewed() {
  trackSegmentEvent('SortMenuViewed', {})
}

export function trackSortOptionClicked(data: SegmentTypes['SortOptionClicked [v1]']) {
  trackSegmentEvent('SortOptionClicked [v1]', data)
}

export function trackSpecialOfferFormSubmittedSuccessfully(data: SegmentTypes['SpecialOfferFormSubmittedSuccessfully [v1]']) {
  trackSegmentEvent('SpecialOfferFormSubmittedSuccessfully [v1]', data)
}

export function trackTripQuotePresented(data: SegmentTypes['TripQuotePresented [v6]']) {
  trackSegmentEvent('TripQuotePresented [v6]', data)
}

export function trackTripQuoteViewed(data: SegmentTypes['TripQuoteViewed [v4]']) {
  trackSegmentEvent('TripQuoteViewed [v4]', data)
}

export function trackUpsellModalOpened(data: SegmentTypes['UpsellModalOpened [v1]']) {
  trackSegmentEvent('UpsellModalOpened [v1]', data)
}

export function trackUpsellPurchaseSubmitted(data: SegmentTypes['UpsellPurchaseSubmitted [v1]']) {
  trackSegmentEvent('UpsellPurchaseSubmitted [v1]', data)
}

export type TripQuoteSegmentPayload = SegmentTypes['TripQuotePresented [v6]'] & SegmentTypes['TripQuoteViewed [v4]']

export function trackError(error: unknown) {
  datadogAddError(error)
}

export function trackSignup(data: SignupTrackArgType) {
  const eventName = data.isOwner ? 'Host Signed Up' : 'Signed Up'

  // Host Signed Up event and Signed Up event have the same properties
  const eventArgs: SegmentTypes['Signed Up [v1]'] = {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    phone: data.phone,
    username: data.username,
    type: data.type,
    title: data.title,
    context: {
      groupId: data.context?.groupId,
    },
  }
  trackSegmentEvent(eventName, eventArgs)

  adalyserTrack({ event: 'lce2' })

  openAIAdsTrack('registration_completed', {
    type: 'customer_action',
  })
}

export function trackRequestBookingSent(data: TrackRequestBookingSentParams, googleAdsId: string) {
  trackSegmentEvent('RequestBookingSent [v7]', data.params)

  // Todo: Does addons, cleaning, insurance, etc. need to be added as products ?
  trackSegmentEvent(
    'Product Added',
    {
      cart_id: data.params.bookingId.toString(),
      product_id: data.params.rvId,
      sku: data.params.rvId,
      category: data.params.rvType,
      name: data.additionalParams.rvName,
      price: roundToNearestDecimal(data.params.rentalPrice ?? 0, 2),
      value: roundToNearestDecimal(data.params.rentalPrice ?? 0, 2),
      quantity: data.params.tripLength,
      url: data.additionalParams.rvUrl,
      currency: data.params.currency,
    })

  googleRemarketingTrack({
    event: 'add_to_cart',
    data: {
      send_to: googleAdsId,
      value: data.params.rentalPrice,
      items: [
        {
          id: data.params.rvId,
          start_date: data.params.startDate,
          end_date: data.params.endDate,
          google_business_vertical: 'hotel_rental',
        },
      ],
    },
  })

  openAIAdsTrack('items_added', {
    type: 'contents',
    amount: roundToNearestCent(data.params.rentalPrice ?? 0) * 100,
    currency: data.params.currency,
    contents: [
      {
        content_type: 'product',
        id: data.params.rvId,
        quantity: 1,
        amount: roundToNearestCent(data.params.rentalPrice ?? 0) * 100,
        currency: data.params.currency,
      },
    ],
  }, {
    event_id: !data.params.bookingId ? String(data.params.bookingId) : undefined,
  })
}

/**
 * Tracks a request booking basics viewed.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/1853980676/RequestBookingBasicsViewed
 * @param data The data to track the request booking basics viewed
 * @returns A promise that resolves when the request booking basics viewed is tracked
 */
export function trackRequestBookingBasicsViewed(data: SegmentTypes['RequestBookingBasicsViewed [v6]']) {
  trackSegmentEvent('RequestBookingBasicsViewed [v6]', data)
}

export function trackRequestBookingBasicsSubmitted(data: SegmentTypes['RequestBookingBasicsSubmitted [v5]']) {
  trackSegmentEvent('RequestBookingBasicsSubmitted [v5]', data)
}

export function trackRequestBookingEnhanceViewed(data: SegmentTypes['RequestBookingEnhanceViewed [v4]']) {
  trackSegmentEvent('RequestBookingEnhanceViewed [v4]', data)
}

export function trackTipsAndResourcesCardClicked(data: SegmentTypes['TipsAndResourcesCardClicked [v2]']) {
  trackSegmentEvent('TipsAndResourcesCardClicked [v2]', data)
}

export function trackSuperHostLandingPageViewed(data: SegmentTypes['SuperHostLandingPageViewed [v1]']) {
  trackSegmentEvent('SuperHostLandingPageViewed [v1]', data)
}

export function trackSuperhostLearnMoreClicked(data: SegmentTypes['SuperhostLearnMoreClicked [v2]']) {
  trackSegmentEvent('SuperhostLearnMoreClicked [v2]', data)
}

export function trackSearchFilterMenuOpened(data: SegmentTypes['SearchFilterMenuOpened [v2]']) {
  trackSegmentEvent('SearchFilterMenuOpened [v2]', data)
}

/**
 * Tracks a search for rv clicked.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2326790158/searchForRvClicked
 * @param data The data to track the search for rv clicked
 * @returns A promise that resolves when the search for rv clicked is tracked
 */
export function trackSearchForRvClicked(data: SegmentTypes['searchForRvClicked [v1]']) {
  trackSegmentEvent('searchForRvClicked [v1]', data)
}

export function trackRequestMoneyClicked(data: SegmentTypes['RequestMoneyClicked [v1]']) {
  trackSegmentEvent('RequestMoneyClicked [v1]', data)
}

/**
 * Tracks a banner clicked.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2711486465/BannerClicked
 * @param data The data to track the banner clicked
 * @returns A promise that resolves when the banner clicked is tracked
 */
export function trackBannerClicked(data: SegmentTypes['BannerClicked [v1]']) {
  trackSegmentEvent('BannerClicked [v1]', data)
}

export function trackWebPushPermissionRequested(data: SegmentTypes['WebPushPermissionRequested [v1]']) {
  trackSegmentEvent('WebPushPermissionRequested [v1]', data)
}

export function trackWebPushPermissionResponded(data: SegmentTypes['WebPushPermissionResponded [v1]']) {
  trackSegmentEvent('WebPushPermissionResponded [v1]', data)
}

export function trackSendMoneyBookingClicked(data: SegmentTypes['SendMoneyBookingClicked [v1]']) {
  trackSegmentEvent('SendMoneyBookingClicked [v1]', data)
}

/**
 * Tracks a send money exited.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2156920874/SendMoneyExited
 * @param data The data to track the send money exited
 * @returns A promise that resolves when the send money exited is tracked
 */
export function trackSendMoneyExited(data: SegmentTypes['SendMoneyExited [v1]']) {
  trackSegmentEvent('SendMoneyExited [v1]', data)
}

/**
 * Tracks a booking details cta clicked.
 *
 * @see https://rvezy.atlassian.net/wiki/spaces/DATA/pages/2400321537/BookingDetailsCtaClicked
 * @param data The data to track the booking details cta clicked
 * @returns A promise that resolves when the booking details cta clicked is tracked
 */
export function trackBookingDetailsCtaClicked(data: SegmentTypes['BookingDetailsCtaClicked [v2]']) {
  trackSegmentEvent('BookingDetailsCtaClicked [v2]', data)
}

export function trackRequestMoneyReviewed(data: SegmentTypes['RequestMoneyReviewed [v1]']) {
  trackSegmentEvent('RequestMoneyReviewed [v1]', data)
}

export function trackHostCalculatorInteraction(data: SegmentTypes['HostCalculatorInteraction [v1]']) {
  trackSegmentEvent('HostCalculatorInteraction [v1]', data)
}

export function trackHostCalculatorViewed(data: SegmentTypes['HostCalculatorViewed [v1]']) {
  trackSegmentEvent('HostCalculatorViewed [v1]', data)
}

export function trackHostCalculatorCtaSubmitted(data: SegmentTypes['HostCalculatorCtaSubmitted [v1]']) {
  trackSegmentEvent('HostCalculatorCtaSubmitted [v1]', data)
}

export function trackListingDraftSubmitted(data: SegmentTypes['ListingDraftSubmitted [v1]']) {
  trackSegmentEvent('ListingDraftSubmitted [v1]', data)
}

export function trackListingDraftCompleted(data: SegmentTypes['ListingDraftCompleted [v1]']) {
  trackSegmentEvent('ListingDraftCompleted [v1]', data)
}

export function trackRecommendedAddOnsButtonClicked(data: SegmentTypes['RecommendedAddOnsButtonClicked [v1]']) {
  trackSegmentEvent('RecommendedAddOnsButtonClicked [v1]', data)
}

export function trackSearchWizardStarted(data: SegmentTypes['SearchWizardStarted [v1]']) {
  trackSegmentEvent('SearchWizardStarted [v1]', data)
}

export function trackSearchWizardCompleted(data: SegmentTypes['SearchWizardCompleted [v1]']) {
  trackSegmentEvent('SearchWizardCompleted [v1]', data)
}

type TripQuoteSegmentPayloadBuildInput = {
  pageSource: string
  rvId: string
  financial: Nullable<FinancialCommon>
  snapshot: {
    chargePerHourOver?: Nullable<number>
    surchargePerExtraKM?: Nullable<number>
    freeHoursPerDay?: Nullable<number>
  }
  booking: {
    dateStart: string
    dateEnd: string
    adults: number
    children: number
    pets: boolean
    destination: string
    rvUsage: string
    deliveryDistance: number
  }
  isShortStay?: boolean
}

export function buildTripQuoteSegmentPayload({
  pageSource,
  rvId,
  financial,
  snapshot,
  booking,
  isShortStay = false,
}: TripQuoteSegmentPayloadBuildInput): TripQuoteSegmentPayload {
  const {
    financialNightlyBase,
    financialNightlyDiscounted,
    financialDiscountsWeekly,
    financialDiscountsMonthly,
    financialTotalRentalAmount,
    financialTotalRoadsides,
    financialTotalInsurances,
    financialInsuranceProtectionLevel,
    financialTotalRenterServiceFee,
    financialTotalCleanings,
    financialTotalSecurityDeposits,
    financialTotalDays,
    financialRenterTotal,
    financialTotalAddOns,
    financialCountry,
    financialTotalNights,
    financialTotalDelivery,
    financialRvAddOns,
  } = getFinancial(financial)

  return {
    rvId,
    startDate: formatAsISOString(booking.dateStart),
    endDate: formatAsISOString(booking.dateEnd),
    adults: booking.adults,
    children: booking.children,
    hasPets: booking.pets,
    pricePerNight: financialNightlyDiscounted || financialNightlyBase,
    smartNightlyRate: 0,
    weeklyDiscount: toNegativeNumber(financialDiscountsWeekly?.DiscountAmount ?? 0),
    monthlyDiscount: toNegativeNumber(financialDiscountsMonthly?.DiscountAmount ?? 0),
    rentalPrice: financialTotalRentalAmount,
    roadsidePrice: financialTotalRoadsides,
    insurancePrice: financialTotalInsurances,
    insuranceType: financialInsuranceProtectionLevel?.toLowerCase() ?? '',
    servicePrice: financialTotalRenterServiceFee,
    cleaningPrice: financialTotalCleanings,
    securityDepositPrice: financialTotalSecurityDeposits,
    generatorPriceHourly: snapshot.chargePerHourOver ?? 0,
    distancePriceKms: snapshot.surchargePerExtraKM ?? 0,
    freeDistanceKms: (snapshot.freeHoursPerDay ?? 0) * financialTotalDays,
    freeGeneratorHours: (snapshot.freeHoursPerDay ?? 0) * financialTotalDays,
    totalPrice: financialRenterTotal,
    addonPrice: financialTotalAddOns,
    currency: lookupCountryCodeToCurrencyCode(financialCountry),
    tripLength: financialTotalNights,
    destination: booking.destination,
    hasDelivery: booking.rvUsage !== 'Pickup',
    deliveryDistance: booking.deliveryDistance,
    deliveryPrice: financialTotalDelivery,
    isStationaryDelivery: booking.rvUsage === 'StationaryAfterDelivery',
    isShortStay,
    addons: financialRvAddOns.map((addon) => ({
      name: addon?.Name ?? '',
      price: addon.PricePerItem,
      pricePer: (addon.IsDaily ? 'night' : 'trip') as 'night' | 'trip',
    })),
    pageSource,
  }
}
